import React, { useEffect, useState } from 'react';
import { useApp } from '../context/AppContext';
import { Button } from '../components/ui/button';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { 
  Users, 
  RefreshCw, 
  FileText, 
  Download,
  CalendarDays,
  ChevronRight,
  Loader2,
  AlertCircle,
  LogOut,
  Calendar
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import GroupDetailView from '../components/GroupDetailView';
import CounselorPanel from '../components/CounselorPanel';
import WeekSummary from '../components/WeekSummary';
import { generatePDF } from '../utils/pdfGenerator';
import { generateCSV } from '../utils/csvGenerator';

export default function DashboardPage({ onSwitchMode }) {
  const { 
    selectedDate, 
    setSelectedDate, 
    campDates, 
    getGroupsForDate,
    getExpectedCountForDate,
    getExpectedGroupCount,
    fetchSheetData,
    loading,
    error,
    campers,
    lastFetchTime,
    getAttendance,
    getCounselorsForDate,
    logout
  } = useApp();

  const [selectedGroup, setSelectedGroup] = useState(null);
  const [view, setView] = useState('attendance');
  const [refreshing, setRefreshing] = useState(false);

  const groups = getGroupsForDate(selectedDate);
  const groupNames = Object.keys(groups).sort((a, b) => {
    if (a === 'Unassigned') return 1;
    if (b === 'Unassigned') return -1;
    return a.localeCompare(b);
  });

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchSheetData();
    setRefreshing(false);
  };

  const handleExportPDF = () => {
    const dateInfo = campDates.find(d => d.date === selectedDate);
    const counselors = getCounselorsForDate(selectedDate);
    generatePDF({
      date: selectedDate,
      fullDate: dateInfo?.fullDate || selectedDate,
      groups,
      getAttendance,
      counselors: [...counselors.active, ...counselors.temporary]
    });
  };

  const handleExportCSV = () => {
    const dateInfo = campDates.find(d => d.date === selectedDate);
    generateCSV({
      date: selectedDate,
      fullDate: dateInfo?.fullDate || selectedDate,
      groups,
      getAttendance
    });
  };

  const logoUrl = 'https://customer-assets.emergentagent.com/job_camp-attendance-app/artifacts/lojn686w_Riverview_Athletics_Logo.png';

  if (selectedGroup) {
    return (
      <GroupDetailView 
        groupName={selectedGroup}
        campers={groups[selectedGroup] || []}
        date={selectedDate}
        onBack={() => setSelectedGroup(null)}
      />
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC] pb-24">
      <Header logoUrl={logoUrl} onLogout={logout} lastFetchTime={lastFetchTime} onSwitchMode={onSwitchMode} />

      {/* Date Tabs */}
      <div className="sticky top-[72px] z-40 bg-white/85 backdrop-blur-xl border-b border-[#E2E8F0] px-4 py-3">
        <div className="max-w-3xl mx-auto">
          <Tabs value={selectedDate} onValueChange={setSelectedDate}>
            <TabsList className="w-full bg-blue-50 p-1 rounded-xl h-auto flex overflow-x-auto">
              {campDates.map((d) => (
                <TabsTrigger
                  key={d.date}
                  value={d.date}
                  data-testid={`date-tab-${d.date}`}
                  className="flex-1 min-w-[70px] py-2 px-3 rounded-lg data-[state=active]:bg-[#0055A4] data-[state=active]:text-white data-[state=active]:shadow-md text-sm font-medium transition-all"
                >
                  <div className="flex flex-col items-center">
                    <span>{d.label}</span>
                    <Badge 
                      variant="secondary" 
                      className="mt-1 text-xs px-1.5 py-0 h-5 bg-white/20 data-[state=active]:bg-white/30"
                    >
                      {getExpectedCountForDate(d.date)}
                    </Badge>
                  </div>
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
        </div>
      </div>

      {/* View Tabs + Actions */}
      <div className="max-w-3xl mx-auto px-4 py-4">
        <div className="flex gap-2 mb-4">
          <Button
            variant={view === 'attendance' ? 'default' : 'outline'}
            onClick={() => setView('attendance')}
            className={`flex-1 rounded-xl font-bold ${view === 'attendance' ? 'bg-[#0055A4]' : ''}`}
            data-testid="view-attendance-button"
          >
            <Users className="w-4 h-4 mr-2" />
            Attendance
          </Button>
          <Button
            variant={view === 'staff' ? 'default' : 'outline'}
            onClick={() => setView('staff')}
            className={`flex-1 rounded-xl font-bold ${view === 'staff' ? 'bg-[#0055A4]' : ''}`}
            data-testid="view-staff-button"
          >
            <Users className="w-4 h-4 mr-2" />
            Staff
          </Button>
          <Button
            variant={view === 'week' ? 'default' : 'outline'}
            onClick={() => setView('week')}
            className={`flex-1 rounded-xl font-bold ${view === 'week' ? 'bg-[#0055A4]' : ''}`}
            data-testid="view-week-button"
          >
            <CalendarDays className="w-4 h-4 mr-2" />
            Week
          </Button>
        </div>

        {/* Export + Refresh Buttons */}
        {view === 'attendance' && (
          <div className="flex gap-2 mb-4">
            <Button variant="outline" onClick={handleExportPDF} className="flex-1 rounded-xl border-2" data-testid="export-pdf-button">
              <FileText className="w-4 h-4 mr-2" />
              Export PDF
            </Button>
            <Button variant="outline" onClick={handleExportCSV} className="flex-1 rounded-xl border-2" data-testid="export-csv-button">
              <Download className="w-4 h-4 mr-2" />
              Export CSV
            </Button>
            <Button
              variant="outline"
              onClick={handleRefresh}
              disabled={refreshing || loading}
              className="rounded-xl border-2"
              data-testid="refresh-button"
            >
              <RefreshCw className={`w-4 h-4 ${refreshing || loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        )}

        {/* Error */}
        {error && (
          <motion.div 
            className="bg-red-50 border border-red-200 rounded-xl p-4 mb-4 flex items-start gap-3"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-red-700 font-medium">Error loading data</p>
              <p className="text-red-600 text-sm mt-1">{error}</p>
              <Button size="sm" onClick={handleRefresh} className="mt-2 bg-red-600 hover:bg-red-700 text-white rounded-lg" data-testid="retry-button">
                Retry
              </Button>
            </div>
          </motion.div>
        )}

        {/* Loading */}
        {loading && (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 text-[#0055A4] animate-spin" />
            <span className="ml-3 text-[#475569]">Loading from Google Sheet...</span>
          </div>
        )}

        {/* Content */}
        <AnimatePresence mode="wait">
          {view === 'attendance' && !loading && (
            <motion.div key="attendance" initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 20 }}>
              {groupNames.length === 0 && campers.length === 0 && !error ? (
                <div className="bg-white rounded-2xl shadow-lg border border-[#E2E8F0] p-8 text-center">
                  <Loader2 className="w-8 h-8 text-[#0055A4] animate-spin mx-auto mb-4" />
                  <h3 className="text-lg font-bold text-[#0F172A] font-['Fredoka']">Loading Camp Data...</h3>
                  <p className="text-[#475569] mt-2">Fetching camper info from the Google Sheet</p>
                </div>
              ) : groupNames.length === 0 ? (
                <div className="bg-white rounded-2xl shadow-lg border border-[#E2E8F0] p-8 text-center">
                  <Users className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-lg font-bold text-[#0F172A] font-['Fredoka']">No Campers for This Day</h3>
                  <p className="text-[#475569] mt-2">No campers are enrolled for the selected date.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {groupNames.map((groupName, index) => (
                    <GroupCard
                      key={groupName}
                      groupName={groupName}
                      campers={groups[groupName]}
                      date={selectedDate}
                      onClick={() => setSelectedGroup(groupName)}
                      index={index}
                    />
                  ))}
                </div>
              )}
            </motion.div>
          )}

          {view === 'staff' && (
            <motion.div key="staff" initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 20 }}>
              <CounselorPanel date={selectedDate} />
            </motion.div>
          )}

          {view === 'week' && (
            <motion.div key="week" initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 20 }}>
              <WeekSummary />
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}

function Header({ logoUrl, onLogout, lastFetchTime, onSwitchMode }) {
  const formattedTime = lastFetchTime 
    ? new Date(lastFetchTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    : null;

  return (
    <div className="sticky top-0 z-50 bg-white/85 backdrop-blur-xl border-b border-[#E2E8F0] shadow-sm">
      <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <img src={logoUrl} alt="Rolling River Day Camp" className="w-12 h-12 rounded-full" />
          <div>
            <h1 className="text-lg font-bold text-[#0F172A] font-['Fredoka']" data-testid="app-title">
              RRDC Attendance
            </h1>
            <p className="text-xs text-[#475569]">
              Holiday Camp Spring 2026
              {formattedTime && <span className="ml-2 text-green-600">Updated {formattedTime}</span>}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-1">
          {onSwitchMode && (
            <Button
              variant="ghost"
              size="icon"
              onClick={onSwitchMode}
              className="rounded-full"
              data-testid="holiday-switch-mode-button"
            >
              <Calendar className="w-5 h-5 text-[#475569]" />
            </Button>
          )}
          <Button
            variant="ghost"
            size="icon"
            onClick={onLogout}
            className="rounded-full"
            data-testid="logout-button"
          >
            <LogOut className="w-5 h-5 text-[#475569]" />
          </Button>
        </div>
      </div>
    </div>
  );
}

function GroupCard({ groupName, campers, date, onClick, index }) {
  const { getAttendance, getExpectedGroupCount } = useApp();
  
  // Use sheet expected count if available, otherwise campers.length
  const groupLetter = groupName.match(/Group ([A-F])/)?.[1] || '';
  const expectedCount = getExpectedGroupCount(date, groupLetter);
  const displayCount = expectedCount !== null ? expectedCount : campers.length;

  const stats = campers.reduce((acc, camper) => {
    const att = getAttendance(camper.id, date);
    acc[att.status] = (acc[att.status] || 0) + 1;
    return acc;
  }, {});

  return (
    <motion.div
      className="bg-white rounded-2xl shadow-[0_4px_20px_-4px_rgba(0,0,0,0.05)] border border-[#E2E8F0] hover:shadow-[0_8px_30px_-4px_rgba(0,0,0,0.1)] transition-all cursor-pointer overflow-hidden"
      onClick={onClick}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05 }}
      data-testid={`group-card-${groupName.replace(/\s+/g, '-').toLowerCase()}`}
    >
      <div className="flex items-center p-5">
        <div className="w-1.5 h-16 bg-[#0055A4] rounded-full mr-4" />
        <div className="flex-1">
          <h3 className="text-lg font-bold text-[#0F172A] font-['Fredoka']">{groupName}</h3>
          <div className="flex items-center gap-3 mt-2">
            <Badge className="bg-blue-100 text-blue-800 hover:bg-blue-100">
              {displayCount} camper{displayCount !== 1 ? 's' : ''}
            </Badge>
            {stats.present > 0 && <span className="text-xs text-green-600">{stats.present} present</span>}
            {stats.not_selected > 0 && <span className="text-xs text-yellow-600 font-semibold">{stats.not_selected} not selected</span>}
            {stats.late > 0 && <span className="text-xs text-amber-600">{stats.late} late</span>}
            {stats.leaving > 0 && <span className="text-xs text-rose-600">{stats.leaving} leaving</span>}
          </div>
        </div>
        <ChevronRight className="w-5 h-5 text-[#94A3B8]" />
      </div>
    </motion.div>
  );
}
